import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { openInquiry } from "@/lib/scroll";
import CAMPAIGN from "@/data/campaign";

const EASE = [0.76, 0, 0.24, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.02, 1.18]);
  const h = CAMPAIGN.hero;

  return (
    <section ref={ref} id="hero" className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y, scale }}>
        <img
          src={h.image}
          alt={h.imageAlt}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#12251d]/85 via-[#12251d]/30 to-[#12251d]/20" />
      </motion.div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
          className="uppercase text-xs tracking-[0.3em] font-semibold text-[#F5F2EB]/80 mb-6"
          data-testid="hero-overline"
        >
          {h.overline}
        </motion.p>

        <h1
          className="font-heading font-light tracking-tighter leading-[0.95] text-[#F5F2EB] text-5xl sm:text-6xl md:text-7xl lg:text-8xl"
          data-testid="hero-headline"
        >
          {h.lines.map((l, i) => (
            <Line key={i} delay={0.3 + i * 0.12}>
              {l.text}
              {l.accent && <em className="not-italic italic font-medium text-[#B7E4BD]">{l.accent}</em>}
            </Line>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.7 }}
          className="mt-8 max-w-xl text-base md:text-lg font-light text-[#F5F2EB]/85 leading-relaxed"
          data-testid="hero-subcopy"
        >
          {h.subcopy}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.9 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6"
        >
          <button
            onClick={openInquiry}
            className="group bg-primary text-primary-foreground font-semibold uppercase tracking-[0.15em] text-sm px-8 py-4 hover:bg-[#2E9440] transition-colors duration-300 flex items-center gap-3"
            data-testid="hero-cta-button"
          >
            Plan my journey
            <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
          </button>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[#F5F2EB]/75 text-xs uppercase tracking-[0.2em]" data-testid="hero-meta">
            {h.meta.map((m, i) => (
              <span key={m} className="flex items-center gap-6">
                <span>{m}</span>
                {i < h.meta.length - 1 && <span className="w-8 h-px bg-[#F5F2EB]/40 hidden sm:block" />}
              </span>
            ))}
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 right-6 md:right-12 z-10 hidden md:flex flex-col items-center gap-3 text-[#F5F2EB]/60"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] [writing-mode:vertical-rl]">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-12 bg-[#F5F2EB]/50"
        />
      </motion.div>
    </section>
  );
};

export default Hero;
